import React, { useState } from 'react';
import { Calendar, Music, Users, Star, ExternalLink, Mail, Phone, MapPin, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const Booking = () => {
  const gigSaladLink = "https://www.gigsalad.com/soulful_pianistvocalist_aka_the_new_lut";
  const { toast } = useToast();
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    message: '',
    venueName: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: formatPhone(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const buildLocationString = () => {
    const parts = [formData.venueName, formData.city, formData.state, formData.zipCode, formData.country].filter(Boolean);
    return parts.join(', ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Required fields missing",
        description: "Please fill in your name, email, and message",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/booking/inquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        event_date: formData.eventDate || null,
        event_location: buildLocationString() || null,
        event_type: formData.eventType || null,
        message: formData.message
      });

      if (response.status === 200) {
        toast({
          title: "Inquiry Sent!",
          description: "Thank you for your booking inquiry. You'll hear back within 24 hours.",
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventDate: '',
          eventType: '',
          message: '',
          venueName: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        });
        setShowLocationDetails(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send inquiry. Please try again later.",
        variant: "destructive"
      });
      console.error('Booking inquiry error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      icon: Music,
      title: "Dueling Pianos",
      description: "High-energy interactive performances that engage and entertain crowds"
    },
    {
      icon: Users,
      title: "Private Events",
      description: "Weddings, corporate events, and special occasions with soulful live music"
    },
    {
      icon: Calendar,
      title: "Venue Performances",
      description: "Regular performances at top venues, bringing unforgettable musical experiences"
    },
    {
      icon: Star,
      title: "Hosting & Curating",
      description: "Expert hosting for intimate events like the Love & Poetry Experience"
    }
  ];

  return (
    <section id="booking" className="py-24 bg-gradient-to-br from-black via-teal-950 to-gray-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-gold-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl animate-pulse animation-delay-2000"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-gold-500/20 backdrop-blur-sm border border-gold-500/30 rounded-full px-6 py-2 mb-6">
              <span className="text-gold-400 font-semibold uppercase tracking-wider text-sm">Book Now</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-teal-50 mb-6">
              Book Brooksgordon for Your Event
            </h1>
            <p className="text-xl text-teal-200 max-w-3xl mx-auto">
              Bring the soulful sound of Brooksgordon to your next event. From intimate gatherings 
              to energetic dueling piano shows, create an unforgettable musical experience.
            </p>
          </div>

          {/* Performance Photos */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="relative rounded-2xl overflow-hidden group aspect-[16/10]">
              <img
                src="https://customer-assets.emergentagent.com/job_d063d7a9-eda0-44cf-9fcf-715c92eeba27/artifacts/7nvv5lz7_image~2.jpg"
                alt="Brooksgordon live on piano with cheering crowd"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <span className="text-gold-400 uppercase tracking-widest text-xs font-semibold">Live Energy</span>
                <h2 className="text-2xl font-bold text-white mt-1">Lighting Up the Stage</h2>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden group aspect-[16/10]">
              <img
                src="https://customer-assets.emergentagent.com/job_d063d7a9-eda0-44cf-9fcf-715c92eeba27/artifacts/4mikzjzb_image~5.jpg"
                alt="Brooksgordon at the piano with vintage microphone, crowd lit by phone lights"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <span className="text-gold-400 uppercase tracking-widest text-xs font-semibold">Soulful Nights</span>
                <h2 className="text-2xl font-bold text-white mt-1">Intimate, Unforgettable Sets</h2>
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <div 
                  key={service.title}
                  className="bg-black/40 backdrop-blur-sm border border-teal-900/30 rounded-xl p-6 text-center hover:border-gold-500/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gold-500/20 rounded-full mb-4">
                    <Icon className="text-gold-400" size={28} />
                  </div>
                  <h2 className="text-xl font-semibold text-teal-50 mb-2">
                    {service.title}
                  </h2>
                  <p className="text-teal-300 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Two Column Layout: GigSalad CTA & Contact Form */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* GigSalad CTA Card */}
            <div className="bg-gradient-to-br from-teal-900/50 to-black/50 backdrop-blur-lg border border-gold-900/30 rounded-3xl p-8 text-center shadow-2xl">
              <div className="space-y-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gold-500/20 backdrop-blur-sm rounded-full mb-4">
                  <Calendar className="text-gold-400" size={40} />
                </div>
                
                <h2 className="text-3xl font-bold text-teal-50">
                  Quick Booking
                </h2>
                
                <p className="text-lg text-teal-200">
                  Book instantly through GigSalad for events within 200 miles of Atlanta
                </p>

                {/* Booking Stats/Features */}
                <div className="grid grid-cols-3 gap-4 py-6">
                  <div className="space-y-2">
                    <div className="text-2xl font-bold text-gold-400">30+</div>
                    <p className="text-teal-300 text-xs">Years Experience</p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold text-gold-400">100%</div>
                    <p className="text-teal-300 text-xs">Professional</p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-bold text-gold-400">★★★★★</div>
                    <p className="text-teal-300 text-xs">Top Rated</p>
                  </div>
                </div>

                {/* CTA Button */}
                <a 
                  href={gigSaladLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gold-500 hover:bg-gold-600 text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-gold-500/50"
                >
                  <Calendar size={24} />
                  Book on GigSalad
                  <ExternalLink size={20} />
                </a>

                <p className="text-teal-400 text-sm">
                  Secure booking • Instant confirmation
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-black/40 backdrop-blur-lg border border-teal-900/30 rounded-3xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/20 rounded-full mb-4">
                  <Mail className="text-teal-400" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-teal-50 mb-2">
                  Direct Inquiry
                </h2>
                <p className="text-teal-200 text-sm">
                  For events over 200 miles from Atlanta or international bookings
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Name *"
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4 items-start">
                  <div>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email *"
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 555-5555"
                      className="w-full px-4 py-3 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                    />
                    <p className="text-teal-400/60 text-xs mt-1.5 ml-1">Contact number</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 items-start">
                  <div className="relative overflow-hidden">
                    <input 
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200 [&:not(:focus):not([value])]:text-transparent [&:not(:focus)]:text-transparent"
                      style={formData.eventDate ? { color: 'rgb(240 253 250)' } : {}}
                    />
                    {!formData.eventDate && (
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-400/50 text-sm pointer-events-none truncate right-10">Date of event</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowLocationDetails(!showLocationDetails)}
                    className="w-full px-4 py-3 bg-black/50 border border-teal-900/50 rounded-lg text-left flex items-center justify-between transition-all duration-200 hover:border-gold-500/50 focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
                  >
                    <span className={buildLocationString() ? 'text-teal-50 truncate pr-2' : 'text-teal-400/50'}>
                      {buildLocationString() || 'Event Location'}
                    </span>
                    {showLocationDetails ? <ChevronUp size={18} className="text-teal-400 flex-shrink-0" /> : <ChevronDown size={18} className="text-teal-400 flex-shrink-0" />}
                  </button>
                </div>

                {/* Location Details Dropdown */}
                {showLocationDetails && (
                  <div className="bg-black/30 border border-teal-900/40 rounded-lg p-4 space-y-3 animate-in fade-in slide-in-from-top-2">
                    <p className="text-teal-300 text-xs font-medium uppercase tracking-wider flex items-center gap-1.5">
                      <MapPin size={12} /> Location Details
                    </p>
                    <div>
                      <input
                        type="text"
                        name="venueName"
                        value={formData.venueName}
                        onChange={handleInputChange}
                        placeholder="Venue Name"
                        className="w-full px-4 py-2.5 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                      />
                      <p className="text-teal-400/60 text-xs mt-1.5 ml-1">If private residence, type &quot;Residence&quot;</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                        className="w-full px-4 py-2.5 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                      />
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="State"
                        className="w-full px-4 py-2.5 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="Zip Code"
                        className="w-full px-4 py-2.5 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                      />
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        placeholder="Country"
                        className="w-full px-4 py-2.5 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                      />
                    </div>
                  </div>
                )}

                <select
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                >
                  <option value="">Select Event Type</option>
                  <option value="wedding">Wedding</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="private">Private Party</option>
                  <option value="dueling-pianos">Dueling Pianos</option>
                  <option value="venue">Venue Performance</option>
                  <option value="other">Other</option>
                </select>

                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your event *"
                  required
                  rows="4"
                  className="w-full px-4 py-3 bg-black/50 border border-teal-900/50 rounded-lg text-teal-50 placeholder-teal-400/50 focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200 resize-none"
                ></textarea>

                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-600 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <MessageSquare size={20} />
                      Send Inquiry
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-gold-500/10 backdrop-blur-sm border border-gold-500/30 rounded-2xl p-6 mb-12">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Phone className="text-gold-400" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gold-400 mb-2">
                  Events Over 200 Miles or International?
                </h3>
                <p className="text-teal-200 leading-relaxed">
                  For bookings farther than 200 miles from Atlanta or international events, please use the 
                  contact form above or call directly for personalized arrangements and travel coordination. 
                  We're excited to bring our music to your location, no matter where you are!
                </p>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="grid md:grid-cols-2 gap-8 text-center md:text-left">
            <div className="bg-black/30 backdrop-blur-sm border border-teal-900/20 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gold-400 mb-3">Performance Venues</h3>
              <p className="text-teal-200 leading-relaxed">
                Regular performer at The Pourhouse in Statesboro, GA and featured host at 
                Atlanta's Chicago Avenue Cafe for the Love & Poetry Experience.
              </p>
            </div>
            <div className="bg-black/30 backdrop-blur-sm border border-teal-900/20 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gold-400 mb-3">Musical Style</h3>
              <p className="text-teal-200 leading-relaxed">
                Soulful vocals reminiscent of Luther Vandross, Brian McKnight, and Will Downing, 
                combined with masterful piano performance honed over 30+ years.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
